import { Stack } from "@mui/material";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { AnonymousPaymentDetailContent } from "Components/AnonymousCustomer/Payment/PaymentDetail/AnonymousPaymentDetailContent";
import { AnonymousTransactionIdEntryForm } from "Components/AnonymousCustomer/Payment/PaymentDetail/AnonymousTransactionIdEntryForm";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useCampaignData } from "Hooks/useCampaignData";
import { Resources, useResource } from "Translations/Resources";
import { useEffect } from "react";
import type { PaymentStep } from "Components/AnonymousCustomer/Payment/config";

const RESOURCE = Resources.TransactionErrors.TransactionNotFoundOrNotAccessible;

type Props = {
  onNextStepRequested: (nextStep?: PaymentStep) => void;
  transactionPublicID: string;
  onError?: () => void;
  campaignCode?: string;
  affiliateCode?: string;
};

export const AnonymousPaymentDetail = ({
  transactionPublicID,
  onNextStepRequested,
  onError,
  campaignCode: campaignCodeProps,
  affiliateCode,
}: Props) => {
  const { t } = useResource();
  const { data, isLoading, isError } = useTransactionsDetail({
    publicID: transactionPublicID,
  });

  const storedCampaignCode = useCampaignData(transactionPublicID);
  const campaignCode = (campaignCodeProps || storedCampaignCode) ?? undefined;

  useEffect(() => {
    if (isError) {
      onError?.();
    }
  }, [isError, onError]);

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  if (isError || !data) {
    return (
      <Stack
        px={3}
        py={4}
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
      >
        <AnonymousTransactionIdEntryForm
          message={
            <ErrorText textAlign="center">
              {t(RESOURCE, { transactionPublicID })}
            </ErrorText>
          }
        />
      </Stack>
    );
  }

  return (
    <AnonymousPaymentDetailContent
      transactionDetail={data}
      transactionPublicID={transactionPublicID}
      onNextStepRequested={onNextStepRequested}
      campaignCode={campaignCode}
      affiliateCode={affiliateCode}
    />
  );
};
